import type Phaser from 'phaser';

import { loadSettings } from '@storage/index';

import { AudioKey, BGM_KEYS, type TBGMTrack } from './audio-keys';

const REWARD_NOTE_GAP_MS = 85; // 結算短音兩段間距
const REWARD_RATE_LOW = 1.1; // 第一段音高
const REWARD_RATE_HIGH = 1.45; // 第二段向上收尾
const REWARD_VOLUME = 0.6; // 不蓋過背景音樂
const BGM_VOLUME = 0.4; // BGM 相對於全域主音量的音量

/** 播放 UI 確認音；未載入或被瀏覽器 autoplay policy 阻擋時不影響操作。 */
export const playUIClick = (scene: Phaser.Scene): void => {
  if (!loadSettings().audioOn || !scene.cache.audio.exists(AudioKey.UIClick)) return;
  try {
    scene.sound.play(AudioKey.UIClick);
  } catch {
    // 音訊播放失敗不應中斷 UI 操作。
  }
};

/** 依玩家設定切換場景 BGM；同一首已播放時不重頭開始。 */
export const syncBackgroundMusic = (scene: Phaser.Scene, track: TBGMTrack): void => {
  const settings = loadSettings();
  for (const key of BGM_KEYS) {
    if (key === track && settings.bgmOn) continue;
    scene.sound.stopByKey(key);
    scene.sound.removeByKey(key);
  }

  if (!settings.bgmOn || !scene.cache.audio.exists(track)) return;
  const current = scene.sound.get(track);
  if (current?.isPlaying) return;
  try {
    (current ?? scene.sound.add(track, { loop: true, volume: BGM_VOLUME })).play();
  } catch {
    // 瀏覽器尚未取得音訊播放權限時，後續使用者操作仍可再次同步。
  }
};

/** 關閉並移除所有原版 BGM。 */
export const stopBackgroundMusic = (scene: Phaser.Scene): void => {
  for (const key of BGM_KEYS) {
    scene.sound.stopByKey(key);
    scene.sound.removeByKey(key);
  }
};

/** 使用既有確認音的兩段音高，給總結算一個短促收尾；遵循音效設定。 */
export const playUIReward = (scene: Phaser.Scene): void => {
  const note = (rate: number): void => {
    if (!loadSettings().audioOn || !scene.cache.audio.exists(AudioKey.UIClick)) return;
    try {
      scene.sound.play(AudioKey.UIClick, { rate, volume: REWARD_VOLUME });
    } catch {
      /* 未解鎖音訊時不阻擋結算。 */
    }
  };
  note(REWARD_RATE_LOW);
  scene.time.delayedCall(REWARD_NOTE_GAP_MS, () => note(REWARD_RATE_HIGH));
};
